import axios from "axios";
import beveatApiResourceOwner from "../../../api/resource_owner_beveat_api.js";
import { useEffect, useMemo, useState } from "react";
import ProgressSpinner from "../../components/progress_spinner.jsx";
import Table from "../../components/table.jsx";
import { INVOICE_PLAN_SYSCODE } from "../../../constants/invoice_plan_syscode.jsx";
import { commonConfigs, urls } from "../../../../config.js";
import { getUser } from "../../../services/local_service.js";
import { InvoiceStatusBadge } from "./invoice_status_badge.jsx";
import UsageCard from "./project_usage_card.jsx";
import { INVOICE_STATUS } from "../../../constants/invoice_status.jsx";
import { INVOICE_SYSCODE } from "../../../constants/invoice_syscode.jsx";

export default function CertificationInvoice() {
  const [load, setLoad] = useState(true);
  const [invoicesData, setInvoicesData] = useState();
  const [ongoingCourses, setOngoingCourses] = useState([]);
  const [plans, setPlans] = useState();
  const [isMobile, setIsMobile] = useState(false);
  const accountId = getUser()?.account_id;
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // Months are 0-indexed, so add 1 to get the current month.

  // Calculate the first day of the current month.
  const firstDayOfMonth = `${currentYear}-${currentMonth
    .toString()
    .padStart(2, "0")}-01`;

  // Calculate the last day of the current month.
  const lastDayOfMonth = new Date(currentYear, currentMonth, 0);
  const lastDayOfMonthFormatted = `${currentYear}-${currentMonth
    .toString()
    .padStart(2, "0")}-${lastDayOfMonth.getDate()}`;

  var StartDateData = sessionStorage.getItem("startdate");
  var EndDateData = sessionStorage.getItem("enddate");

  const [isApplying, setIsApplying] = useState(false);
  const [isReseting, setIsReseting] = useState(false);

  // Initialize startdate and enddate as empty strings to show all invoices by default
  const [startdate, setStartDate] = useState("");
  const [enddate, setEndDate] = useState("");

  const [startdateFilter, setStartDateFilter] = useState(startdate);
  const [enddateFilter, setEndDateFilter] = useState(enddate);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const resetFilter = () => {
    setIsReseting(true);
    setLoad(true);
    setStartDate("");
    setEndDate("");
    sessionStorage.setItem("startdate", "");
    sessionStorage.setItem("enddate", "");
    setStartDateFilter("");
    setEndDateFilter("");
  };

  const applyFilter = () => {
    setIsApplying(true); // Disable the "APPLY" button
    setLoad(true);

    // If no start or end date is selected, default to showing all invoices
    const startFilter = StartDateData.length > 0 ? StartDateData : startdate;
    const endFilter = EndDateData.length > 0 ? EndDateData : enddate;

    setStartDateFilter(startFilter);
    setEndDateFilter(endFilter);
  };

  async function getCertificationPlans() {
    const getCertificationPlansUrl =
      urls.resourceOwnerUrl +
      commonConfigs.apiUrls.getCertificationPlans();
    await beveatApiResourceOwner.get(getCertificationPlansUrl, {
      headers: commonConfigs.resourceOwnerApiHeader
    })
      .then((res) => {
        setPlans(res.data.result.data);
        getDeveloperCourse(res.data.result.data).catch((err) => {
          throw err;
        });
      })
      .catch((err) => {
        console.log(err);
      });
  }

  async function getCertificationInvoices() {
    const getInvoicesUrl =
      urls.resourceOwnerUrl +
      commonConfigs.apiUrls.getInvoices(
        accountId,
        INVOICE_SYSCODE.Certification,
        startdateFilter,
        enddateFilter,
        "",
        "",
        ""
      );
    await beveatApiResourceOwner.get(getInvoicesUrl, {
      headers: commonConfigs.resourceOwnerApiHeader
    })
      .then((res) => {
        // console.log(res.data.result.data);
        const paidInvoices = res.data.result.data.filter(invoice => invoice.invoice_status === INVOICE_STATUS.Invoice_Paid);
        setInvoicesData(paidInvoices);
        setIsApplying(false);
        setIsReseting(false);
        setLoad(false);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  async function getDeveloperCourse(certPlans) {
    const getDeveloperCourseUrl =
      urls.resourceOwnerUrl + commonConfigs.apiUrls.getDeveloperCourses(accountId, '', 0);

    await beveatApiResourceOwner.get(getDeveloperCourseUrl, {
      headers: commonConfigs.resourceOwnerApiHeader
    })
      .then((res) => {
        let ongoingCourse = [];
        if (Array.isArray(res.data.result.data)) {
          res.data.result.data.map(course => {
            const match = certPlans.find(plan => plan.org_invoice_plan_id === course.org_invoice_plan_id)
            ongoingCourse.push({
              ...course,
              expiry: course.course_expiry,
              start_datetime: course.course_start_datetime,
              plan_name: match.invoice_plan
            })
          })
        } else {
          const match = certPlans.find(plan => plan.org_invoice_plan_id === res.data.result.data.org_invoice_plan_id);
          ongoingCourse.push({
            ...course,
            expiry: course.course_expiry,
            start_datetime: course.course_start_datetime,
            plan_name: match.invoice_plan
          })
        }
        setOngoingCourses(ongoingCourse)
      })
      .catch((err) => {
        console.log(err);
      });
  }

  useEffect(() => {
    if (load) {
      getCertificationInvoices().catch((err) => {
        console.log("err: " + err);
      });
      getCertificationPlans().catch((err) => {
        console.log("err: " + err);
      });
    }
  }, [load, startdateFilter, enddateFilter]);

  const columns = useMemo(() => [
    {
      Header: "Invoice No.",
      accessor: "txn_invoice_id",
      // Cell: (row) => row.row.index + 1,
    },
    {
      Header: "Plan Name",
      accessor: "txn_invoice_details",
      Cell: ({ value }) => {
        // const match = plans.find(plan => plan.org_invoice_plan_id === value);
        return value[0].invoice_plan;
      },
    },
    {
      Header: "Billing Period",
      accessor: "invoice_billing_period",
      Cell: ({ row }) => {
        const { invoice_start_datetime, invoice_end_datetime } = row.original;
        const startDate = new Date(invoice_start_datetime)
          .toISOString()
          .split("T")[0];
        const endDate = new Date(invoice_end_datetime)
          .toISOString()
          .split("T")[0];
        return `${startDate} to ${endDate}`;
      },
    },
    {
      Header: "Paid Date",
      accessor: "invoice_paid_datetime",
      Cell: ({ value }) => {
        const paidDate = new Date(value)
          .toISOString()
          .split("T")[0];
        return paidDate ? paidDate : '-';
      },
    },
    {
      Header: "Subtotal Amount",
      accessor: "invoice_sub_total_amt",
    },
    {
      Header: "Coursework Code",
      accessor: "course_code",
      Cell: ({ row }) => {
        const { voucher_code, voucher_syscode } = row.original;
        return voucher_syscode && (voucher_syscode === 6 || 7) ? voucher_code : "-";
      },
    },
    {
      Header: "Voucher Code",
      accessor: "voucher_code",
      Cell: ({ row }) => {
        const { voucher_code, voucher_syscode } = row.original;
        return voucher_syscode && !(voucher_syscode === 6 || 7) ? voucher_code : "-";
      },
    },
    {
      Header: "Voucher Amount",
      accessor: "voucher_amt",
    },
    {
      Header: "Discount Percent",
      accessor: "discount_percent",
    },
    {
      Header: "Discount Amount",
      accessor: "discount_amt",
    },
    {
      Header: "Total Nett Amount",
      accessor: "invoice_total_nett_amt",
    },
    {
      Header: "Status",
      accessor: "invoice_status",
      Cell: ({ cell: { value } }) => <InvoiceStatusBadge value={value} />,
    },
  ]);

  return (
    <>
      {/* Usage Cards Section */}
      <div
        className={`mb-2 flex flex-grow gap-x-5 ${isMobile ? 'justify-center' : 'justify-end'}`}
        style={{
          display: isMobile ? "flex" : "grid",
          flexDirection: isMobile ? "column" : undefined,
          gridTemplateColumns: isMobile ? undefined : `repeat(${Math.min(ongoingCourses.length, 3)}, 3fr)`,
          gap: isMobile ? "15px" : undefined,
        }}
      >
        {ongoingCourses.map((course, index) => {
          return (
            <div key={index} style={{ width: isMobile ? "100%" : undefined }}>
              <UsageCard data={course} />
            </div>
          )
        })}
      </div>

      {/* Filter Section */}
      <div
        style={{
          backgroundColor: "#FFF",
          padding: isMobile ? "16px" : "20px",
          borderRadius: "10px",
          margin: isMobile ? "15px 0" : "20px 0 20px 0",
          textAlign: "left",
          color: "black",
        }}
      >
        <div style={{ display: isMobile ? "block" : "flex" }}>
          <div style={{ 
            width: "100%", 
            padding: isMobile ? "5px 0" : "10px",
            marginBottom: isMobile ? "15px" : "0"
          }}>
            <div
              className="font-bold"
              style={{ 
                paddingTop: isMobile ? "5px" : "10px", 
                paddingBottom: isMobile ? "8px" : "10px" 
              }}
            >
              <h6 style={{ 
                marginBottom: "0px",
                fontSize: isMobile ? "14px" : undefined
              }}>
                Billing Period Start Date
              </h6>
            </div>
            <input
              type="date"
              style={{ 
                width: "100%", 
                padding: isMobile ? "12px" : "10px", 
                borderRadius: "8px",
                fontSize: isMobile ? "16px" : undefined,
                minHeight: isMobile ? "44px" : undefined,
                border: "1px solid #d1d5db"
              }}
              value={startdate}
              onChange={(e) => {
                setStartDate(e.target.value);
                sessionStorage.setItem("startdate", e.target.value);
              }}
              placeholder="dd/mm/yyyy"
            />
          </div>
          <div style={{ 
            width: "100%", 
            padding: isMobile ? "5px 0" : "10px",
            marginBottom: isMobile ? "20px" : "0"
          }}>
            <div
              className="font-bold"
              style={{ 
                paddingTop: isMobile ? "5px" : "10px", 
                paddingBottom: isMobile ? "8px" : "10px" 
              }}
            >
              <h6 style={{ 
                marginBottom: "0px",
                fontSize: isMobile ? "14px" : undefined
              }}>
                Billing Period End Date
              </h6>
            </div>
            <input
              type="date"
              style={{ 
                width: "100%", 
                padding: isMobile ? "12px" : "10px", 
                borderRadius: "8px",
                fontSize: isMobile ? "16px" : undefined,
                minHeight: isMobile ? "44px" : undefined,
                border: "1px solid #d1d5db"
              }}
              value={enddate}
              onChange={(e) => {
                setEndDate(e.target.value);
                sessionStorage.setItem("enddate", e.target.value);
              }}
              placeholder="dd/mm/yyyy"
            />
          </div>
        </div>
        
        {/* Buttons Section */}
        <div style={{ 
          textAlign: isMobile ? "center" : "end", 
          paddingTop: isMobile ? "20px" : "34px"
        }}>
          <div style={{
            display: isMobile ? "flex" : "inline-block",
            flexDirection: isMobile ? "column" : undefined,
            gap: isMobile ? "10px" : undefined,
            width: isMobile ? "100%" : undefined
          }}>
            <button
              data-inline="true"
              type="button"
              className={`${isMobile ? 'w-full' : 'w-full'} inline-flex justify-center rounded-full border border-transparent shadow-sm px-12 py-2 text-base font-bold ${isMobile ? '' : 'sm:ml-2 sm:w-auto'} sm:text-sm ${isApplying
                ? "bg-blue-500 text-white cursor-not-allowed"
                : "hover:bg-blue-500 text-blue-500 hover:text-white outline outline-blue-500"
                }`}
              onClick={() => applyFilter()}
              disabled={isApplying}
              style={{
                minHeight: isMobile ? "44px" : undefined,
                fontSize: isMobile ? "16px" : undefined,
                marginBottom: isMobile ? "0" : undefined
              }}
            >
              {isApplying ? "Applying..." : "Apply"}
            </button>
            <button
              data-inline="true"
              type="button"
              className={`${isMobile ? 'w-full' : 'w-full'} inline-flex justify-center rounded-full border border-transparent shadow-sm px-12 py-2 text-base font-bold ${isMobile ? '' : 'sm:ml-2 sm:w-auto'} sm:text-sm ${isReseting
                ? "bg-red-500 text-white cursor-not-allowed"
                : "hover:bg-red-500 text-red-500 hover:text-white outline outline-red-500"
                }`}
              onClick={() => resetFilter()}
              disabled={isReseting}
              style={{
                minHeight: isMobile ? "44px" : undefined,
                fontSize: isMobile ? "16px" : undefined
              }}
            >
              {isReseting ? "Reseting..." : "Reset"}
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      {invoicesData != null ? (
        <div style={{ 
          overflowX: isMobile ? "auto" : "visible",
          marginBottom: isMobile ? "20px" : undefined
        }}>
          <Table
            columns={columns}
            data={invoicesData}
            filters={columns.map((column) => column.accessor)}
          />
        </div>
      ) : (
        <div className={`${isMobile ? 'w-full h-96' : 'w-screen h-screen'} flex flex-wrap flex-row place-items-center`}>
          <div className="flex place-content-center w-full">
            <div className="text-center">
              <ProgressSpinner className={`${isMobile ? 'w-12 h-12' : 'w-20 h-20'}`} />
              {isMobile && (
                <p className="mt-4 text-gray-600 text-sm">
                  Loading certification invoices...
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}